import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PageWrapper } from '../../components';
import venueListsApiService from '../../services/venueListsApi';
import './VenueLists.css';

const normalizeIdentity = (value) => (value == null ? '' : String(value).trim().toLowerCase());

const formatDate = (value) => {
  if (!value) return 'No event date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const isSessionExpiredError = (error) => error?.message === 'SESSION_EXPIRED';

const VenueListDetail = () => {
  const { listId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const [venueList, setVenueList] = useState(null);
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [commentByItem, setCommentByItem] = useState({});
  const [invitePayload, setInvitePayload] = useState({
    mode: 'email',
    target: '',
    role: 'voter'
  });
  const [newItem, setNewItem] = useState({
    venueId: '',
    notes: ''
  });
  const [editableTitle, setEditableTitle] = useState('');
  const [editableDescription, setEditableDescription] = useState('');

  const actorUserId =
    userProfile?.backend_user_id ||
    userProfile?.id ||
    userProfile?.firebase_uid ||
    currentUser?.uid ||
    null;
  const actorEmail = normalizeIdentity(currentUser?.email || userProfile?.email);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const loadList = useCallback(async () => {
    if (!listId) return;
    try {
      setLoading(true);
      setError('');
      const [detailData, resultsData] = await Promise.all([
        venueListsApiService.getVenueList(listId),
        venueListsApiService.getVenueListResults(listId).catch(() => null)
      ]);
      const detailList = detailData?.venue_list || detailData?.list || detailData || null;
      const detailItems = Array.isArray(detailData?.items) ? detailData.items : [];
      const detailMembers = Array.isArray(detailData?.members) ? detailData.members : [];

      setVenueList(detailList);
      setItems(detailItems);
      setMembers(detailMembers);
      setResults(resultsData);
      setEditableTitle(detailList?.title || '');
      setEditableDescription(detailList?.description || '');
    } catch (loadError) {
      if (isSessionExpiredError(loadError)) {
        setError('Session expired. Please sign in again.');
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        navigate('/', { replace: true });
        return;
      }
      setError(loadError?.message || 'Failed to load venue list details.');
      setVenueList(null);
      setItems([]);
      setMembers([]);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [listId, navigate]);

  useEffect(() => {
    if (!currentUser || !listId) return;
    loadList();
  }, [currentUser, listId, loadList]);

  const myMember = useMemo(() => {
    const matched = members.find((member) => {
      const memberUserId = normalizeIdentity(member?.user_id);
      const memberEmail = normalizeIdentity(member?.email || member?.invited_email);
      return (actorUserId && memberUserId === normalizeIdentity(actorUserId)) || (actorEmail && memberEmail === actorEmail);
    });
    if (matched) return matched;
    if (actorUserId && normalizeIdentity(venueList?.owner_user_id) === normalizeIdentity(actorUserId)) {
      return { role: 'owner', invite_status: 'accepted' };
    }
    return null;
  }, [members, actorUserId, actorEmail, venueList]);

  const currentRole = myMember?.role || 'viewer';
  const currentStatus = String(venueList?.status || 'active').toLowerCase();
  const isLocked = currentStatus === 'locked';
  const isArchived = currentStatus === 'archived';
  const isMutable = !isLocked && !isArchived;
  const canEdit = isMutable && (currentRole === 'owner' || currentRole === 'editor');
  const canInvite = canEdit;
  const canVote = isMutable && (currentRole === 'owner' || currentRole === 'editor' || currentRole === 'voter');
  const canManageRoles = currentRole === 'owner' && isMutable;
  const canLock = currentRole === 'owner' && currentStatus === 'active';

  const resultsByItem = useMemo(() => {
    const map = {};
    const resultItems = Array.isArray(results?.items) ? results.items : [];
    resultItems.forEach((itemResult) => {
      const key = itemResult?.list_item_id || itemResult?.item_id;
      if (key) map[key] = itemResult;
    });
    return map;
  }, [results]);

  const runAction = async (action, successText) => {
    try {
      setSaving(true);
      setError('');
      setMessage('');
      await action();
      await loadList();
      if (successText) setMessage(successText);
    } catch (actionError) {
      if (isSessionExpiredError(actionError)) {
        setError('Session expired. Please sign in again.');
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        navigate('/', { replace: true });
        return;
      }
      setError(actionError?.message || 'Action failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMetadata = async (event) => {
    event.preventDefault();
    if (!venueList?.id) return;
    if (!editableTitle.trim()) {
      setError('Title is required.');
      return;
    }
    await runAction(
      () =>
        venueListsApiService.updateVenueList(venueList.id, {
          title: editableTitle.trim(),
          description: editableDescription.trim()
        }),
      'List details updated.'
    );
  };

  const handleAddItem = async (event) => {
    event.preventDefault();
    if (!venueList?.id || !newItem.venueId.trim()) {
      setError('Venue ID is required.');
      return;
    }
    await runAction(
      () =>
        venueListsApiService.addVenueListItem(venueList.id, {
          venue_id: newItem.venueId.trim(),
          ...(newItem.notes.trim() ? { notes: newItem.notes.trim() } : {})
        }),
      'Venue added to list.'
    );
    setNewItem({ venueId: '', notes: '' });
  };

  const handleVote = async (itemId, vote) => {
    if (!venueList?.id || !itemId) return;
    await runAction(
      () =>
        venueListsApiService.voteOnVenueListItem(venueList.id, {
          list_item_id: itemId,
          vote,
          ...(commentByItem[itemId]?.trim() ? { comment: commentByItem[itemId].trim() } : {})
        }),
      'Vote saved.'
    );
  };

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!venueList?.id) return;
    if (!invitePayload.target.trim()) {
      setError('Invite target is required.');
      return;
    }

    const invite =
      invitePayload.mode === 'user'
        ? { user_id: invitePayload.target.trim(), role: invitePayload.role }
        : { email: invitePayload.target.trim(), role: invitePayload.role };

    try {
      setSaving(true);
      setError('');
      setMessage('');
      const response = await venueListsApiService.inviteVenueListMembers(venueList.id, [invite]);
      await loadList();
      const inviteToken = response?.members?.[0]?.invite_token || response?.invite_token || null;
      if (inviteToken) {
        setMessage(`Invite created. One-time token: ${inviteToken}`);
      } else {
        setMessage('Invite sent.');
      }
      setInvitePayload((prev) => ({ ...prev, target: '' }));
    } catch (inviteError) {
      if (isSessionExpiredError(inviteError)) {
        setError('Session expired. Please sign in again.');
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        navigate('/', { replace: true });
        return;
      }
      setError(inviteError?.message || 'Failed to invite member.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMemberRole = async (memberId, role) => {
    if (!venueList?.id || !memberId) return;
    await runAction(() => venueListsApiService.updateMemberRole(venueList.id, memberId, role), 'Member role updated.');
  };

  const handleRemoveMember = async (memberId) => {
    if (!venueList?.id || !memberId) return;
    await runAction(() => venueListsApiService.removeMember(venueList.id, memberId), 'Member removed.');
  };

  const handleRemoveItem = async (itemId) => {
    if (!venueList?.id || !itemId) return;
    await runAction(() => venueListsApiService.removeVenueListItem(venueList.id, itemId), 'Venue removed.');
  };

  const handleLockList = async () => {
    if (!venueList?.id) return;
    await runAction(() => venueListsApiService.lockVenueList(venueList.id), 'List locked.');
  };

  const handleSetStatus = async (status) => {
    if (!venueList?.id) return;
    await runAction(() => venueListsApiService.updateVenueList(venueList.id, { status }), `Status updated to ${status}.`);
  };

  if (!currentUser) return null;

  return (
    <PageWrapper useNewFooter useLanding2Header>
      <section className="venue-lists-page">
        <div className="venue-lists-shell">
          <div className="venue-list-detail-header">
            <Link className="venue-list-back-link" to="/venue-lists">
              Back to venue lists
            </Link>
            <span className={`venue-list-status venue-list-status--${currentStatus}`}>{currentStatus}</span>
          </div>

          {error ? <div className="venue-lists-error">{error}</div> : null}
          {message ? <div className="venue-lists-success">{message}</div> : null}

          {loading ? (
            <p className="venue-lists-note">Loading list details...</p>
          ) : null}

          {!loading && venueList ? (
            <>
              <form className="venue-lists-card" onSubmit={handleSaveMetadata}>
                <div className="venue-lists-card-header">
                  <h1>List details</h1>
                  {canEdit ? (
                    <button type="submit" disabled={saving}>
                      {saving ? 'Saving...' : 'Save details'}
                    </button>
                  ) : null}
                </div>
                <div className="venue-lists-create-grid">
                  <label>
                    <span>Title</span>
                    <input
                      type="text"
                      value={editableTitle}
                      onChange={(e) => setEditableTitle(e.target.value)}
                      disabled={!canEdit}
                    />
                  </label>
                  <label>
                    <span>Event date</span>
                    <input type="text" value={formatDate(venueList.event_date)} disabled />
                  </label>
                  <label className="venue-lists-create-full">
                    <span>Description</span>
                    <textarea
                      rows={3}
                      value={editableDescription}
                      onChange={(e) => setEditableDescription(e.target.value)}
                      disabled={!canEdit}
                    />
                  </label>
                </div>
                <div className="venue-lists-inline-actions">
                  {canLock ? (
                    <button type="button" onClick={handleLockList} disabled={saving}>
                      Lock list
                    </button>
                  ) : null}
                  {currentRole === 'owner' && currentStatus === 'locked' ? (
                    <>
                      <button type="button" onClick={() => handleSetStatus('active')} disabled={saving}>
                        Unlock list
                      </button>
                      <button type="button" onClick={() => handleSetStatus('archived')} disabled={saving}>
                        Archive list
                      </button>
                    </>
                  ) : null}
                </div>
              </form>

              <div className="venue-lists-card">
                <div className="venue-lists-card-header">
                  <h2>Venues in this list</h2>
                </div>
                {canEdit ? (
                  <form className="venue-lists-inline-form" onSubmit={handleAddItem}>
                    <input
                      type="text"
                      placeholder="Venue ID"
                      value={newItem.venueId}
                      onChange={(e) => setNewItem((prev) => ({ ...prev, venueId: e.target.value }))}
                      disabled={saving}
                    />
                    <input
                      type="text"
                      placeholder="Notes (optional)"
                      value={newItem.notes}
                      onChange={(e) => setNewItem((prev) => ({ ...prev, notes: e.target.value }))}
                      disabled={saving}
                    />
                    <button type="submit" disabled={saving || !newItem.venueId.trim()}>
                      Add venue
                    </button>
                  </form>
                ) : null}

                <div className="venue-list-items">
                  {items.length === 0 ? (
                    <p className="venue-lists-note">No venues yet.</p>
                  ) : (
                    items.map((item) => {
                      const itemId = item?.id || item?.list_item_id;
                      const itemResult = resultsByItem[itemId] || null;
                      return (
                        <article key={itemId} className="venue-list-item">
                          <div className="venue-list-item-top">
                            <div>
                              <h4>{item?.venue_name || `Venue ${item?.venue_id || ''}`}</h4>
                              <p>ID: {item?.venue_id || 'Unknown'}</p>
                              {item?.notes ? <p>{item.notes}</p> : null}
                            </div>
                            {canEdit ? (
                              <button
                                type="button"
                                className="venue-lists-danger-btn"
                                onClick={() => handleRemoveItem(itemId)}
                                disabled={saving}
                              >
                                Remove
                              </button>
                            ) : null}
                          </div>

                          <div className="venue-list-item-stats">
                            <span>Yes {itemResult?.yes_count || 0}</span>
                            <span>Maybe {itemResult?.maybe_count || 0}</span>
                            <span>No {itemResult?.no_count || 0}</span>
                            <span>Score {itemResult?.score || 0}</span>
                          </div>

                          {canVote ? (
                            <div className="venue-list-vote-box">
                              <div className="venue-list-vote-actions">
                                {['yes', 'maybe', 'no'].map((voteValue) => (
                                  <button
                                    key={voteValue}
                                    type="button"
                                    className={itemResult?.my_vote === voteValue ? 'is-active' : ''}
                                    onClick={() => handleVote(itemId, voteValue)}
                                    disabled={saving}
                                  >
                                    {voteValue}
                                  </button>
                                ))}
                              </div>
                              <textarea
                                rows={2}
                                placeholder="Optional vote comment"
                                value={commentByItem[itemId] || ''}
                                onChange={(e) =>
                                  setCommentByItem((prev) => ({
                                    ...prev,
                                    [itemId]: e.target.value
                                  }))
                                }
                                disabled={saving}
                              />
                              <button
                                type="button"
                                onClick={() => handleVote(itemId, itemResult?.my_vote || 'maybe')}
                                disabled={saving}
                              >
                                Save comment
                              </button>
                            </div>
                          ) : null}
                        </article>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="venue-lists-two-col">
                <div className="venue-lists-card">
                  <h2>Members</h2>
                  {canInvite ? (
                    <form className="venue-lists-inline-form" onSubmit={handleInvite}>
                      <select
                        value={invitePayload.mode}
                        onChange={(e) => setInvitePayload((prev) => ({ ...prev, mode: e.target.value, target: '' }))}
                        disabled={saving}
                      >
                        <option value="email">Invite by email</option>
                        <option value="user">Invite by user ID</option>
                      </select>
                      <input
                        type="text"
                        placeholder={invitePayload.mode === 'email' ? 'teammate@email.com' : 'user-id'}
                        value={invitePayload.target}
                        onChange={(e) => setInvitePayload((prev) => ({ ...prev, target: e.target.value }))}
                        disabled={saving}
                      />
                      <select
                        value={invitePayload.role}
                        onChange={(e) => setInvitePayload((prev) => ({ ...prev, role: e.target.value }))}
                        disabled={saving}
                      >
                        <option value="editor">editor</option>
                        <option value="voter">voter</option>
                        <option value="viewer">viewer</option>
                      </select>
                      <button type="submit" disabled={saving || !invitePayload.target.trim()}>
                        Invite
                      </button>
                    </form>
                  ) : null}

                  <div className="venue-list-members">
                    {members.length === 0 ? (
                      <p className="venue-lists-note">No members found.</p>
                    ) : (
                      members.map((member) => {
                        const memberId = member?.id || member?.member_id;
                        const memberLabel = member?.email || member?.invited_email || member?.user_id || 'Member';
                        const isPrimaryOwner = normalizeIdentity(member?.user_id) === normalizeIdentity(venueList?.owner_user_id);
                        return (
                          <div key={memberId} className="venue-list-member-row">
                            <div>
                              <p>{memberLabel}</p>
                              <span>
                                {member?.invite_status || 'accepted'} - {member?.role || 'viewer'}
                              </span>
                            </div>
                            <div className="venue-list-member-actions">
                              {canManageRoles && !isPrimaryOwner ? (
                                <select
                                  value={member?.role || 'viewer'}
                                  onChange={(e) => handleUpdateMemberRole(memberId, e.target.value)}
                                  disabled={saving}
                                >
                                  <option value="editor">editor</option>
                                  <option value="voter">voter</option>
                                  <option value="viewer">viewer</option>
                                </select>
                              ) : null}
                              {canInvite && !isPrimaryOwner ? (
                                <button
                                  type="button"
                                  className="venue-lists-danger-btn"
                                  onClick={() => handleRemoveMember(memberId)}
                                  disabled={saving}
                                >
                                  Remove
                                </button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="venue-lists-card">
                  <h2>Results summary</h2>
                  {results?.overall ? (
                    <div className="venue-list-results-overall">
                      <p>Total members: {results.overall.member_count || 0}</p>
                      <p>Eligible voters: {results.overall.eligible_voter_count || 0}</p>
                      <p>Voted members: {results.overall.voted_member_count || 0}</p>
                      <p>Completion: {results.overall.completion_percent || 0}%</p>
                    </div>
                  ) : (
                    <p className="venue-lists-note">No result data yet.</p>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </section>
    </PageWrapper>
  );
};

export default VenueListDetail;
